/* eslint-disable no-console */
import { useState, useCallback } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';
import { authService } from '../services/auth-service';
import { getTimestampFromJWT, processAuthEvents } from '../utils/auth-utils';
import { FEATURE_SWITCH_NAMES, DEFAULT_AUTH_EVENT_FILTER_HOURS } from '../utils/consts';
import { IAuthenticationEvent } from '../models/common-models';

interface UseAuthActivityReturn {
  matchedAuthEvent: IAuthenticationEvent | null;
  isAccountActivityLoading: boolean;
  fetchUserAuthActivity: (jwt: string) => Promise<void>;
  clearAuthEvent: () => void;
}

export const useAuthActivity = (): UseAuthActivityReturn => {
  const [matchedAuthEvent, setMatchedAuthEvent] = useState<IAuthenticationEvent | null>(null);
  const [isAccountActivityLoading, setIsAccountActivityLoading] = useState<boolean>(false);

  // Get configurable auth event filter hours (defaults to 48 hours)
  const authEventFilterHours = parseInt(
    (useConfigService(FEATURE_SWITCH_NAMES.AUTH_EVENT_FILTER_HOURS) as string)
    || DEFAULT_AUTH_EVENT_FILTER_HOURS.toString(),
    10
  );

  const fetchUserAuthActivity = useCallback(async (jwtToken: string) => {
    if (!jwtToken) {
      console.error('fetchUserAuthActivity: Cannot fetch user auth activity - JWT token is missing or null');
      return;
    }

    setIsAccountActivityLoading(true);

    try {
      const result = await authService.fetchUserAuthActivity(jwtToken);
      if (result.success && result.data) {
        // Decode JWT token on successful auth activity fetch
        const decodedJWT = getTimestampFromJWT(jwtToken);

        if (decodedJWT) {
          const matchedEvent = await processAuthEvents(
            result.data.events,
            decodedJWT,
            authEventFilterHours
          );

          setMatchedAuthEvent(matchedEvent);
        } else {
          console.error('Failed to extract timestamp from JWT');
          setMatchedAuthEvent(null);
        }
      } else {
        setMatchedAuthEvent(null);
      }
    } catch (err) {
      console.error('fetchUserAuthActivity: Failed to fetch user auth activity:', err);
      setMatchedAuthEvent(null);
    } finally {
      setIsAccountActivityLoading(false);
    }
  }, [authEventFilterHours]);

  const clearAuthEvent = useCallback(() => {
    setMatchedAuthEvent(null);
  }, []);

  // Note: Removed automatic fetching - auth activity is now only fetched
  // when scan-to-pay buttons are clicked, not on component mount

  return {
    matchedAuthEvent,
    isAccountActivityLoading,
    fetchUserAuthActivity,
    clearAuthEvent,
  };
};
